import { CondensedButton, IconDownloadLine, Text, View } from "@instructure/ui";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Card } from "../../../components/Card";
import {
  ERROR,
  useNotification,
} from "../../../components/CustomHooks/NotificationProvider";
import { rteHasContent } from "../../../components/RichTextArea/RichTextEditor";
import RichTextViewer from "../../../components/RichTextArea/RichTextViewer";
import { Files } from "../../../services/FilesService";
import downloadFile from "../../../utils/Dom";

/**
 * PublicStructuredQuestionItem — Read-only view of a single structured question
 * for the public results page. Fetches its own file list and handles download.
 *
 * Props:
 * @param {Object}  question     — { id, question_text, helper_text, file_upload_enabled, ... }
 * @param {number}  index        — 0-based index for labeling
 * @param {string}  measureId    — The ID of the qualitative measure
 * @param {string}  tenantId     — The tenant ID for public file access
 * @param {string}  responseText — Fetched response content for this question
 * @param {boolean} hasError     — Whether fetching the response failed
 * @param {boolean} isLast       — Whether this is the last question in the list
 */
const PublicStructuredQuestionItem = ({
  question,
  index,
  measureId,
  tenantId,
  responseText,
  hasError = false,
  isLast,
}) => {
  const { t } = useTranslation();
  const showNotification = useNotification();
  const [files, setFiles] = useState([]);

  useEffect(() => {
    if (!question.file_upload_enabled) return;

    const fetchFiles = async () => {
      try {
        let paths = await Files.getPublicFiles(
          "public-qualitativeEvidence",
          `${measureId}/${question.id}`,
          tenantId
        );
        paths = paths.filter((file) => file.file_name !== "response.html");
        setFiles(paths);
      } catch {
        setFiles([]);
      }
    };

    fetchFiles();
  }, [measureId, question, tenantId]);

  function handleDownload() {
    try {
      files.forEach((element) => {
        downloadFile(element.file_url, element.file_name);
      });
    } catch {
      showNotification(
        t("notifications.assessmentResults.downloadMeasureEvidenceError"),
        ERROR
      );
    }
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "0.75rem" }}>
      <Text weight="bold" size="medium">
        {question.question_text}
      </Text>

      {rteHasContent(question.helper_text) && (
        <RichTextViewer
          value={question.helper_text}
          ariaLabel={t("structuredQuestions.helperTextFor", {
            number: index + 1,
          })}
        />
      )}

      <View
        as="div"
        borderWidth={isLast ? "none" : "0 0 small 0"}
        padding={isLast ? "none" : "0 0 small 0"}
      >
        <Card style={{ border: "1px solid #ccc", backgroundColor: "#fff" }}>
          {hasError ? (
            <Text color="danger">
              {t("notifications.assessmentResults.questionRetrievalError", {
                questionNumber: index + 1,
              })}
            </Text>
          ) : rteHasContent(responseText) ? (
            <RichTextViewer
              value={responseText}
              ariaLabel={t("results.measureResults")}
            />
          ) : (
            <Text fontStyle="italic">{t("results.noResponsePublished")}</Text>
          )}
          {files.length > 0 && (
            <View
              as="div"
              borderWidth="small 0 0 0"
              textAlign="end"
              margin="small 0 0 0"
              padding="small 0 0 0"
            >
              <CondensedButton
                renderIcon={IconDownloadLine}
                onClick={handleDownload}
              >
                {`${t("results.downloadMeasureEvidence")} (${files.length})`}
              </CondensedButton>
            </View>
          )}
        </Card>
      </View>
    </div>
  );
};

export default PublicStructuredQuestionItem;
